/**
 * «قبل ما نأكد الحجز» — the pre-visit questions (المرحلة 1.2, P02).
 *
 * When the booked institution's `preVisitIntake` policy asks before booking,
 * the server refuses the booking with 422 PRE_VISIT_REQUIRED and the questions
 * it is missing — only those (lib/preVisit.js). This window asks them and hands
 * the answers back; the booking screen sends the same booking again with
 * `preVisit: { answers }`. The questions the institution marked optional are
 * offered on the same screen and never hold the booking.
 *
 * Under the default policy («reception») the server never refuses, this never
 * opens, and booking is exactly what it was.
 *
 *   const gate = usePreVisitGate();
 *   const submit = async (answers = gate.answers) => {
 *     try { await create({ ...payload, ...withAnswers(answers) }) }
 *     catch (err) { if (gate.catches(err)) return; … }
 *   };
 *   <PreVisitGate gate={gate} onAnswered={submit} />
 */

import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Field, Icon, Input, Modal, Select, Textarea } from '../../components/common';
import { useI18n } from '../../i18n/I18nProvider';

/** What a booking carries once the patient has answered something. */
export const withAnswers = (answers) => (answers && Object.keys(answers).length ? { preVisit: { answers } } : {});

/** The gate's state for one booking screen: what was asked and what was answered. */
export function usePreVisitGate() {
  const [request, setRequest] = useState(null);
  const [answers, setAnswers] = useState({});
  const catches = useCallback((err) => {
    if (!err || err.status !== 422 || !err.body || err.body.code !== 'PRE_VISIT_REQUIRED') return false;
    setRequest(err.body);
    return true;
  }, []);
  return { request, answers, setAnswers, catches, close: () => setRequest(null) };
}

/** A question's label: the server's own wording, or the key the screens translate. */
function useLabel() {
  const { t } = useI18n();
  return (q) => (q.labelKey ? t(q.labelKey) : q.label);
}

function Question({ q, value, onChange, required }) {
  const { t } = useI18n();
  const label = useLabel()(q);
  const testId = `pv-${q.key.replace(/[^\w]+/g, '-')}`;
  if (q.type === 'boolean') {
    return (
      <Field label={label} required={required}>
        <div className="hp-choice" role="radiogroup">
          {[true, false].map((v) => (
            <button key={String(v)} type="button" role="radio" aria-checked={value === v} className={`hp-chip ${value === v ? 'is-on' : ''}`} onClick={() => onChange(v)} data-testid={`${testId}-${v}`}>
              {v ? t('common.yes') : t('common.no')}
            </button>
          ))}
        </div>
      </Field>
    );
  }
  if (q.type === 'select') {
    return (
      <Field label={label} required={required}>
        <Select value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
          <option value="">{t('preVisit.choose')}</option>
          {(q.options || []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </Select>
      </Field>
    );
  }
  if (q.type === 'number') {
    return (
      <Field label={label} required={required}>
        <Input type="number" inputMode="decimal" value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
      </Field>
    );
  }
  if (q.key === 'chiefComplaint') {
    return (
      <Field label={label} required={required}>
        <Textarea rows={3} value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={t('preVisit.complaintHint')} data-testid={testId} />
      </Field>
    );
  }
  return (
    <Field label={label} required={required}>
      <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </Field>
  );
}

/**
 * @param {object}   gate        usePreVisitGate()
 * @param {Function} onAnswered  send the booking again with these answers
 * @param {boolean}  busy        the booking is being sent
 */
export function PreVisitGate({ gate, onAnswered, busy = false }) {
  const { t, lang } = useI18n();
  const [draft, setDraft] = useState({});
  const request = gate.request;
  if (!request) return null;

  const missing = request.missing || [];
  const optional = request.optional || [];
  const valueOf = (key) => (key in draft ? draft[key] : gate.answers[key]);
  const blank = (v) => v === undefined || v === null || (typeof v === 'string' && !v.trim());
  const ready = missing.every((q) => !blank(valueOf(q.key)));
  const profile = request.profile || {};
  // What this provider needs from the profile, by name: its percentage counts only its own required steps, so it is not shown beside the home card's.
  const needed = (profile.missingSteps || []).map((s) => t(`healthProfile.steps.${s}`)).join(lang === 'en' ? ', ' : '، ');

  const submit = () => {
    const answers = { ...gate.answers, ...draft };
    gate.setAnswers(answers);
    setDraft({});
    gate.close();
    onAnswered(answers);
  };

  return (
    <Modal
      open
      onClose={gate.close}
      title={t('preVisit.title')}
      icon="clipboard-check"
      footer={
        <div className="hp-actions">
          <Button icon="check" onClick={submit} disabled={!ready} loading={busy} data-testid="pv-submit">
            {t('preVisit.submit')}
          </Button>
          <Button variant="ghost" onClick={gate.close}>
            {t('common.cancel')}
          </Button>
        </div>
      }
    >
      <div className="pv-gate" data-testid="pre-visit-gate">
        <p className="pv-gate__intro">{t('preVisit.intro')}</p>
        {profile.ready === false && (
          <p className="hp-note hp-note--warn">
            <Icon name="alert-triangle" size={14} />
            <span>
              {needed ? t('preVisit.profileNeeds', { steps: needed }) : t('preVisit.profileBelow', { n: profile.completeness ?? 0, threshold: profile.threshold ?? 0 })}{' '}
              <Link to={profile.missingSteps && profile.missingSteps[0] ? `/patient/health-profile?step=${profile.missingSteps[0]}` : '/patient/health-profile'}>{t('preVisit.completeProfile')}</Link>
            </span>
          </p>
        )}
        <div className="pv-gate__questions">
          {missing.map((q) => (
            <Question key={q.key} q={q} required value={valueOf(q.key)} onChange={(v) => setDraft({ ...draft, [q.key]: v })} />
          ))}
          {optional.length > 0 && <p className="pv-gate__optional">{t('preVisit.optional')}</p>}
          {optional.map((q) => (
            <Question key={q.key} q={q} value={valueOf(q.key)} onChange={(v) => setDraft({ ...draft, [q.key]: v })} />
          ))}
        </div>
      </div>
    </Modal>
  );
}

export default PreVisitGate;
